import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { getFirestore, collection, getDocs } from '@firebase/firestore'
import { app } from '../../firebase.js'
import {
  BarChart,
  Bar,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import Table from 'react-bootstrap/Table'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowsRotate, faChartColumn, faGlobe, faMagnifyingGlass, faUsers } from '@fortawesome/free-solid-svg-icons'
import LeftBar from '../../Components/LeftBar'
import Dashnav from '../../Components/Dashnav'
import '../../Pages/dash.css'

const convertedStatuses = ['Student', 'Completed', 'Deferred', 'Expelled', 'Dropped']
const notConvertedStatuses = ['inactive', 'Did Not Join']

const chartColors = {
  converted: '#0f766e',
  unconverted: '#f59e0b'
}

const countFormatter = new Intl.NumberFormat('en-KE')

const formatCount = (value) => countFormatter.format(Number(value || 0))

const formatPercent = (value) => {
  const safeValue = Number.isFinite(value) ? value : 0
  return `${safeValue.toFixed(1)}%`
}

const normalizeText = (value) => String(value || '').trim()

const normalizeStatus = (value) => normalizeText(value).toLowerCase()

const convertedStatusSet = new Set(convertedStatuses.map(normalizeStatus))
const notConvertedStatusSet = new Set(notConvertedStatuses.map(normalizeStatus))

const CountryTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) {
    return null
  }

  const row = payload[0]?.payload || {}

  return (
    <div className="distributionChartTooltip">
      <strong>{label}</strong>
      <div className="distributionChartTooltipRow">
        <span>Converted</span>
        <strong>{formatCount(row.converted)}</strong>
      </div>
      <div className="distributionChartTooltipRow">
        <span>Unconverted</span>
        <strong>{formatCount(row.unconverted)}</strong>
      </div>
      <div className="distributionChartTooltipRow">
        <span>Total</span>
        <strong>{formatCount(row.total)}</strong>
      </div>
      <div className="distributionChartTooltipRow">
        <span>Conversion rate</span>
        <strong>{formatPercent(row.conversionRate)}</strong>
      </div>
    </div>
  )
}

function DistributionByCountry() {
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [rows, setRows] = useState([])

  const fetchData = useCallback(async (options = {}) => {
    const isInitialLoad = options.initial === true

    if (isInitialLoad) {
      setLoading(true)
    } else {
      setRefreshing(true)
    }

    setError('')

    try {
      const db = getFirestore(app)
      const usersRef = collection(db, 'users')
      const snapshot = await getDocs(usersRef)

      const countryMap = {}

      snapshot.forEach((docItem) => {
        const user = docItem.data()

        if (normalizeStatus(user.userType) !== 'student') {
          return
        }

        const country = normalizeText(user.nationality) || 'Unknown'
        const status = normalizeStatus(user.userStatus)

        if (!countryMap[country]) {
          countryMap[country] = {
            country,
            converted: 0,
            unconverted: 0
          }
        }

        if (convertedStatusSet.has(status)) {
          countryMap[country].converted += 1
        } else if (notConvertedStatusSet.has(status)) {
          countryMap[country].unconverted += 1
        }
      })

      const countryRows = Object.values(countryMap)
        .map((row) => {
          const total = row.converted + row.unconverted
          return {
            ...row,
            total,
            conversionRate: total > 0 ? (row.converted / total) * 100 : 0
          }
        })
        .filter((row) => row.total > 0)
        .sort((first, second) => {
          if (second.total !== first.total) {
            return second.total - first.total
          }

          if (second.converted !== first.converted) {
            return second.converted - first.converted
          }

          return first.country.localeCompare(second.country)
        })

      setRows(countryRows)
    } catch (fetchError) {
      console.error('Failed to load country distribution:', fetchError)
      setRows([])
      setError('We could not load the country distribution right now. Please try again.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    fetchData({ initial: true })
  }, [fetchData])

  const filteredRows = useMemo(() => {
    const search = searchTerm.trim().toLowerCase()

    if (!search) {
      return rows
    }

    return rows.filter((row) => {
      return row.country.toLowerCase().includes(search)
    })
  }, [rows, searchTerm])

  const chartRows = useMemo(() => filteredRows.slice(0, 8), [filteredRows])

  const stats = useMemo(() => {
    return rows.reduce(
      (accumulator, row) => {
        accumulator.total += row.total
        accumulator.converted += row.converted
        accumulator.unconverted += row.unconverted
        return accumulator
      },
      { total: 0, converted: 0, unconverted: 0 }
    )
  }, [rows])

  const overallConversionRate = stats.total > 0 ? (stats.converted / stats.total) * 100 : 0
  const topCountries = rows.slice(0, 5)
  const hasCountryData = rows.length > 0
  const emptyStateMessage = hasCountryData
    ? 'No countries match the current search.'
    : 'No tracked country records were found yet.'

  return (
    <div className="clientsComp applicationsPage distributionByCountryPage">
      <LeftBar />
      <div className="dashMainContent">
        <Dashnav />

        <div className="appsHeader">
          <div className="attendanceHeaderCopy">
            <p className="appsKicker">Data Analytics</p>
            <h4>Distribution by Country</h4>
            <p className="appsSubtext">
              Compare where students are coming from and how each country is performing across converted and unconverted records.
            </p>
          </div>
          <div className="appsHeaderStats">
            <div className="appsStatChip">
              <span>Tracked students</span>
              <strong>{formatCount(stats.total)}</strong>
            </div>
            <div className="appsStatChip">
              <span>Countries</span>
              <strong>{formatCount(rows.length)}</strong>
            </div>
            <div className="appsStatChip">
              <span>Converted</span>
              <strong>{formatCount(stats.converted)}</strong>
            </div>
            <div className="appsStatChip">
              <span>Conversion rate</span>
              <strong>{formatPercent(overallConversionRate)}</strong>
            </div>
          </div>
        </div>

        <div className="appsFiltersCard distributionFiltersCard">
          <div className="appsSearch input-group">
            <span className="input-group-text">
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </span>
            <Form.Control
              type="text"
              placeholder="Search country..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
          <div className="distributionFiltersMeta">
            <span>{formatCount(filteredRows.length)} countries visible</span>
            <span>{chartRows.length ? `Charting top ${formatCount(chartRows.length)} countries` : 'No countries to chart'}</span>
          </div>
          <Button variant="outline-dark" onClick={() => fetchData()} disabled={loading || refreshing}>
            <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>

        {error && (
          <div className="incomeStateCard mb-3">
            <p>{error}</p>
            <Button variant="outline-dark" onClick={() => fetchData()} disabled={refreshing}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              Try again
            </Button>
          </div>
        )}

        <div className="distributionChartGrid">
          <div className="appsTableCard distributionChartCard">
            <div className="appsTableHeader">
              <h6>
                <FontAwesomeIcon icon={faChartColumn} className="me-2" />
                Country mix
              </h6>
              <span>Top {formatCount(chartRows.length)} countries by tracked students</span>
            </div>
            <div className="distributionChartWrap">
              {loading ? (
                <div className="incomeStateCard">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading country distribution...</span>
                  </Spinner>
                  <p>Loading country distribution...</p>
                </div>
              ) : filteredRows.length === 0 ? (
                <div className="incomeStateCard">
                  <FontAwesomeIcon icon={faGlobe} size="2x" className="mb-1 text-secondary" />
                  <p>{emptyStateMessage}</p>
                </div>
              ) : (
                <div className="distributionChartBox">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartRows}
                      layout="vertical"
                      margin={{ top: 8, right: 18, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                      <XAxis
                        type="number"
                        allowDecimals={false}
                        tickFormatter={formatCount}
                        tick={{ fontSize: 12, fill: '#64748b' }}
                        axisLine={{ stroke: '#cbd5e1' }}
                        tickLine={false}
                      />
                      <YAxis
                        type="category"
                        dataKey="country"
                        width={120}
                        tick={{ fontSize: 12, fill: '#475569' }}
                        axisLine={{ stroke: '#cbd5e1' }}
                        tickLine={false}
                      />
                      <Tooltip content={<CountryTooltip />} />
                      <Legend verticalAlign="top" height={28} iconType="circle" wrapperStyle={{ fontSize: '12px', color: '#64748b' }} />
                      <Bar dataKey="converted" name="Converted" stackId="status" fill={chartColors.converted} radius={[0, 0, 0, 0]} />
                      <Bar dataKey="unconverted" name="Unconverted" stackId="status" fill={chartColors.unconverted} radius={[0, 10, 10, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          <div className="appsInsightsCard distributionInsightsCard">
            <div className="appsInsightsHeader">
              <h6>
                <FontAwesomeIcon icon={faGlobe} className="me-2" />
                Leading countries
              </h6>
              <span>{formatCount(topCountries.length)} shown</span>
            </div>
            <div className="appsInsightsList">
              {topCountries.map((country) => (
                <div className="appsInsightsItem" key={country.country}>
                  <div>
                    <strong>{country.country}</strong>
                    <span>{formatCount(country.total)} tracked students</span>
                  </div>
                  <small>{formatPercent(country.conversionRate)} converted</small>
                </div>
              ))}
            </div>
            <p className="appsInsightsMore">
              Search to narrow the chart and table, or refresh to pull the latest country breakdown.
            </p>
          </div>
        </div>

        <div className="appsTableCard distributionTableCard">
          <div className="appsTableHeader">
            <h6>
              <FontAwesomeIcon icon={faUsers} className="me-2" />
              Country breakdown
            </h6>
            <span>{formatCount(filteredRows.length)} results</span>
          </div>
          <div className="appsTableWrap">
            {loading ? (
              <div className="incomeStateCard">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading country breakdown...</span>
                </Spinner>
                <p>Loading country breakdown...</p>
              </div>
            ) : (
              <Table hover responsive className="appsTable distributionTable">
                <thead>
                  <tr>
                    <th>Country</th>
                    <th>Tracked</th>
                    <th>Converted</th>
                    <th>Unconverted</th>
                    <th>Conversion Rate</th>
                    <th>Status Mix</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={row.country}>
                      <td>
                        <strong>{row.country}</strong>
                      </td>
                      <td>{formatCount(row.total)}</td>
                      <td>{formatCount(row.converted)}</td>
                      <td>{formatCount(row.unconverted)}</td>
                      <td>{formatPercent(row.conversionRate)}</td>
                      <td>
                        <div className="distributionCountryMix">
                          <div className="distributionCountryMeter" aria-hidden="true">
                            <span
                              className="distributionCountryMeterFill"
                              style={{ width: `${Math.min(Math.max(row.conversionRate, 0), 100)}%` }}
                            />
                          </div>
                          <div className="distributionCountryStats">
                            <span>{formatCount(row.converted)} converted</span>
                            <span>{formatCount(row.unconverted)} unconverted</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredRows.length === 0 && (
                    <tr>
                      <td colSpan="6" className="appsEmptyState">
                        {emptyStateMessage}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DistributionByCountry
